import React from "react";
import PropTypes from "prop-types";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { useTermStructure } from "./useTermStructure";

const BAND_COLOR = "hsl(var(--chart-1))";
const BAND_FILL_OPACITY = 0.22;
const EDGE_COLOR = "hsl(var(--chart-1))";
const GRID_COLOR = "hsl(var(--chart-grid))";
const AXIS_COLOR = "hsl(var(--chart-axis))";

// pct formats a fraction as a signed percentage, e.g. 0.18 → "+18%", -0.02 → "-2%".
function pct(v) {
  const n = Number(v) || 0;
  return `${n > 0 ? "+" : ""}${(n * 100).toFixed(1)}%`;
}

// TermStructureTooltip shows the per-lead band edges + sample size.
function TermStructureTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0] && payload[0].payload;
  if (!p) return null;
  return (
    <div className="rounded-ctrl border border-card-bd bg-card px-3 py-2 text-xs shadow-float">
      <div className="font-semibold text-text">Lead {p.lead} day{p.lead === 1 ? "" : "s"}</div>
      <div className="mt-1 text-text-2">
        High edge: <span className="font-medium">{pct(p.high_frac)}</span>
      </div>
      <div className="text-text-2">
        Low edge: <span className="font-medium">{pct(p.low_frac)}</span>
      </div>
      <div className="mt-1 text-text-3">n = {p.n} snapshot{p.n === 1 ? "" : "s"}</div>
    </div>
  );
}
TermStructureTooltip.propTypes = { active: PropTypes.bool, payload: PropTypes.array };

// TermStructurePanel renders the lead-conditional band-width term structure (conformal v3
// "Object A"): for each horizon (lead) the relative band edges low_frac/high_frac. The band
// typically widens with lead. Read-only; fetched on mount.
export default function TermStructurePanel() {
  const { points, loading, error } = useTermStructure();

  // Recharts needs a contiguous band: the upper area paints from low_frac to high_frac
  // by stacking a transparent base at low_frac plus a span of (high - low).
  const data = (points || []).map((p) => ({
    ...p,
    base: p.low_frac,
    span: p.high_frac - p.low_frac,
  }));

  return (
    <section className="rounded-card border border-card-bd bg-card p-4" aria-labelledby="term-structure-heading">
      <div className="flex items-baseline justify-between">
        <h3 id="term-structure-heading" className="text-sm font-semibold text-text">
          Term structure
        </h3>
        <span className="text-xs text-text-3">band width vs horizon (lead-conditional)</span>
      </div>

      {error ? (
        <div className="py-10 text-center text-sm text-text-3">
          Could not load the term structure.
        </div>
      ) : loading ? (
        <div className="py-10 text-center text-sm text-text-3">Loading…</div>
      ) : data.length === 0 ? (
        <div className="py-10 text-center text-sm text-text-3">
          Not enough snapshot history yet — the term structure appears once forecasts have been
          tracked across several daily snapshots.
        </div>
      ) : (
        <div className="mt-3" style={{ width: "100%", height: 240 }} data-testid="term-structure-chart">
          <ResponsiveContainer>
            <ComposedChart data={data} margin={{ top: 10, right: 16, bottom: 8, left: 0 }} stackOffset="none">
              <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="lead"
                type="number"
                tick={{ fontSize: 12, fill: AXIS_COLOR }}
                tickLine={false}
                axisLine={{ stroke: GRID_COLOR }}
                tickMargin={8}
                label={{ value: "Lead (days)", position: "insideBottom", offset: -2, fontSize: 11, fill: AXIS_COLOR }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: AXIS_COLOR }}
                tickLine={false}
                axisLine={false}
                width={56}
                tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
              />
              <Tooltip
                content={<TermStructureTooltip />}
                cursor={{ stroke: AXIS_COLOR, strokeWidth: 1, strokeDasharray: "3 3" }}
              />
              <ReferenceLine y={0} stroke={AXIS_COLOR} strokeWidth={1} />
              {/* transparent base lifts the visible span to start at low_frac */}
              <Area type="monotone" dataKey="base" stackId="band" stroke="none" fill="none" isAnimationActive={false} />
              <Area type="monotone" dataKey="span" stackId="band" stroke="none" fill={BAND_COLOR} fillOpacity={BAND_FILL_OPACITY} isAnimationActive={false} />
              {/* explicit edges so the band's top/bottom are legible */}
              <Line type="monotone" dataKey="high_frac" stroke={EDGE_COLOR} strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="low_frac" stroke={EDGE_COLOR} strokeWidth={2} dot={false} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
